import React, {
    useState,
    useRef,
    forwardRef,
    useImperativeHandle,
    useEffect
} from 'react';
import ReactDOM from 'react-dom';
import { ReactComponent as MicroPhoneIcon } from '../../../../ui/src/assets/studies-icons/MicroPhoneIcon.svg';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from 'axios';
import ApiServices from '../../shared/ApiServices/ApiServices';
import RecModal from './TranscriptionModal';
import { WaveToBlobService } from '@eduvidu/node';
import './VoiceRecording.css';

interface VoiceRecordingProps {
    variant?: 'default' | 'header';
    onText?: (text: string, isFinal?: boolean) => void;
    className?: string;
}

export interface VoiceRecordingHandle {
    startListening: () => void;
    stopListening: () => void;
    pauseListening: () => void;
    resumeListening: () => void;
    isListening: () => boolean;
}

const VoiceRecording = forwardRef<VoiceRecordingHandle, VoiceRecordingProps>(
    ({ variant = 'default', onText, className }, ref) => {
        const [finalTranscript, setFinalTranscript] = useState('');
        const [interimTranscript, setInterimTranscript] = useState('');
        const [listening, setListening] = useState(false);
        const [paused, setPaused] = useState(false);
        const [isModalOpen, setIsModalOpen] = useState(false);
        const [loading, setLoading] = useState(false);
        const [duration, setDuration] = useState('00:00');
        const [voiceBlob, setVoiceBlob] = useState<Blob | null>(null);
        const [studyId, setStudyId] = useState('');

        const recognitionRef = useRef<any>(null);
        const mediaRecorderRef = useRef<MediaRecorder | null>(null);
        const mediaStreamRef = useRef<MediaStream | null>(null);
        const audioChunks = useRef<any[]>([]);
        const startTimeRef = useRef<Date | null>(null);
        const durationIntervalRef = useRef<any>(null);
        const pauseDurationRef = useRef<number>(0);
        const location = useLocation();

        useEffect(() => {
            const id = new URLSearchParams(location.search).get('studyId');
            if (id) setStudyId(id);
        }, [location]);

        useImperativeHandle(ref, () => ({
            startListening,
            stopListening,
            pauseListening,
            resumeListening,
            isListening: () => listening
        }));

        const SpeechRecognition = (window as any).webkitSpeechRecognition;

        const openModal = () => setIsModalOpen(true);
        const closeModal = () => setIsModalOpen(false);

        const initializeRecognition = () => {
            if (!SpeechRecognition) {
                toast.error('Your browser does not support Speech Recognition.');
                return;
            }

            const recognition = new SpeechRecognition();
            recognition.continuous = true;
            recognition.interimResults = true;
            recognition.lang = 'en-US';

            recognition.onresult = (event: any) => {
                let finalText = '';
                let interimText = '';
                for (let i = event.resultIndex; i < event.results.length; ++i) {
                    const transcript = event.results[i][0].transcript;
                    if (event.results[i].isFinal) {
                        finalText += transcript;
                    } else {
                        interimText += transcript;
                    }
                }

                if (finalText) {
                    setFinalTranscript(prev =>
                        prev + (prev && !prev.endsWith(' ') ? ' ' : '') + finalText
                    );
                    onText?.(finalText, true);
                }

                if (interimText) {
                    setInterimTranscript(interimText);
                    onText?.(interimText, false);
                }
            };

            recognition.onstart = () => {
                setListening(true);
                setPaused(false);
                startTrackingDuration();
                startRecordingAudio();
            };

            recognition.onend = () => {
                if (!paused) {
                    setListening(false);
                    stopTrackingDuration();
                    stopRecordingAudio();
                }
            };

            recognition.onerror = (event: any) => {
                if (event.error === 'not-allowed') {
                    toast.error('Microphone access denied.');
                } else if (event.error !== 'aborted') {
                    toast.error(`${event.error}`);
                }
            };

            recognitionRef.current = recognition;

            navigator.mediaDevices.getUserMedia({ audio: true }).then((stream) => {
                mediaStreamRef.current = stream;
                mediaRecorderRef.current = new MediaRecorder(stream);

                mediaRecorderRef.current.ondataavailable = (event) => {
                    audioChunks.current.push(event.data);
                };

                mediaRecorderRef.current.onstop = () => {
                    const audioBlob = new Blob(audioChunks.current, { type: 'audio/wav' });
                    setVoiceBlob(audioBlob);
                    audioChunks.current = [];
                };

                mediaRecorderRef.current.start();
            }).catch((err) => {
                toast.error('Error accessing the microphone');
                console.error('Microphone error:', err);
            });
        };

        const startRecordingAudio = () => {
            if (mediaRecorderRef.current?.state === 'inactive') {
                mediaRecorderRef.current.start();
            }
        };

        const stopRecordingAudio = () => {
            if (mediaRecorderRef.current?.state === 'recording') {
                mediaRecorderRef.current.stop();
            }
        };

        const startTrackingDuration = () => {
            startTimeRef.current = new Date();
            durationIntervalRef.current = setInterval(() => {
                const now = Date.now();
                const start = startTimeRef.current?.getTime() || now;
                const elapsed = Math.floor((now - start) / 1000) + pauseDurationRef.current;
                const minutes = Math.floor(elapsed / 60);
                const seconds = elapsed % 60;
                setDuration(`${minutes < 10 ? '0' : ''}${minutes}:${seconds < 10 ? '0' : ''}${seconds}`);
            }, 1000);
        };

        const stopTrackingDuration = () => {
            clearInterval(durationIntervalRef.current);
            durationIntervalRef.current = null;
        };

        const startListening = () => {
            if (recognitionRef.current && listening) recognitionRef.current.stop();
            initializeRecognition();
            recognitionRef.current?.start();
        };

        const stopListening = () => {
            recognitionRef.current?.stop();
            mediaRecorderRef.current?.stop();
            setListening(false);
            setPaused(false);
            stopTrackingDuration();
            pauseDurationRef.current = 0;

            if (mediaStreamRef.current) {
                mediaStreamRef.current.getTracks().forEach(track => track.stop());
                mediaStreamRef.current = null;
            }
        };

        const pauseListening = () => {
            recognitionRef.current?.stop();
            setTimeout(() => {
                setPaused(true);
                setListening(false);
                stopTrackingDuration();
                const elapsed = Math.floor((Date.now() - (startTimeRef.current?.getTime() || 0)) / 1000);
                pauseDurationRef.current += elapsed;
                startTimeRef.current = null;
            }, 50);
        };

        const resumeListening = () => {
            if (!listening) {
                if (!recognitionRef.current) initializeRecognition();
                recognitionRef.current?.start();
                setPaused(false);
                setListening(true);
                startTimeRef.current = new Date();
                setTimeout(() => startTrackingDuration(), 1);
            }
        };

        const saveTranscriptionFromModal = async () => {
            if (loading) return;
            setLoading(true);

            if (mediaRecorderRef.current?.state === 'recording') {
                mediaRecorderRef.current.requestData();

                setTimeout(async () => {
                    if (audioChunks.current.length === 0) {
                        toast.error('No audio data available yet.');
                        setLoading(false);
                        return;
                    }

                    try {
                        const partialBlob = new Blob(audioChunks.current, { type: 'audio/wav' });
                        const wavBlob = await WaveToBlobService.getWaveBlob(partialBlob, true);

                        // console.log('Exported Audio During Recording:', wavBlob);
                        // const audioUrl = URL.createObjectURL(wavBlob);
                        // const audio = new Audio(audioUrl);
                        // audio.play();
                        const presigned = await ApiServices.getVoiceDictationPreAssignedUrl(studyId);
                        const formData = new FormData();
                        Object.entries(presigned.fields).forEach(([key, val]) => {
                            formData.append(key, val as string);
                        });
                        formData.append('file', wavBlob);
                        await axios.post(presigned.url, formData);

                        toast.success('Voice dictation uploaded successfully.');
                        resetTranscription();
                        closeModal();
                    } catch (error) {
                        console.error(error);
                        toast.error('Failed to save during recording.');
                    } finally {
                        setLoading(false);
                    }
                }, 300);
            } else {
                try {
                    if (!voiceBlob) {
                        toast.error('No audio recorded.');
                        setLoading(false);
                        return;
                    }

                    const wavBlob = await WaveToBlobService.getWaveBlob(voiceBlob, true);
                    // console.log('Exported Audio:', wavBlob);
                    // const audioUrl = URL.createObjectURL(wavBlob);
                    // const audio = new Audio(audioUrl);
                    // audio.play();
                    const presigned = await ApiServices.getVoiceDictationPreAssignedUrl(studyId);
                    const formData = new FormData();
                    Object.entries(presigned.fields).forEach(([key, val]) => {
                        formData.append(key, val as string);
                    });
                    formData.append('file', wavBlob);
                    await axios.post(presigned.url, formData);

                    toast.success('Voice dictation uploaded successfully.');
                    resetTranscription();
                    closeModal();
                } catch (error) {
                    console.error(error);
                    toast.error('Failed to upload voice dictation.');
                } finally {
                    setLoading(false);
                }
            }
        };

        const resetTranscription = () => {
            recognitionRef.current?.abort();
            recognitionRef.current = null;
            setFinalTranscript('');
            setInterimTranscript('');
            setListening(false);
            setPaused(false);
            setDuration('00:00');
            pauseDurationRef.current = 0;
            audioChunks.current = [];

            if (mediaStreamRef.current) {
                mediaStreamRef.current.getTracks().forEach(track => track.stop());
                mediaStreamRef.current = null;
            }
        };

        const cancelTranscription = () => {
            resetTranscription();
            closeModal();
        };

        const canSave = finalTranscript.trim() || interimTranscript.trim();

        const renderMicButton = () => {
            const baseClass =
                variant === 'header' ? 'mic-button mic-header' : 'mic-button mic-default';
            const listeningClass = listening ? 'mic-listening' : '';

            return (
                <div className="flex items-center justify-center">
                    <MicroPhoneIcon
                        role="button"
                        aria-label={listening ? 'Stop Recording' : 'Start Recording'}
                        className={`${baseClass} ${listeningClass} ${className || ''}`}
                        onClick={listening ? stopListening : resumeListening}
                    />
                </div>
            );
        };

        return (
            <>
                {variant === 'default' && renderMicButton()}

                {variant === 'header' && (
                    <div className="flex h-[2rem] flex-col items-center justify-center">{renderMicButton()}</div>
                )}

                {variant === 'header' && listening && ReactDOM.createPortal(
                    <div
                        className={[
                            'fixed z-[9999] bottom-5 right-5 sm:bottom-7 sm:right-7 md:bottom-8 md:right-8',
                            'flex items-center gap-2.5 px-4 py-2.5 rounded-xl',
                            'bg-[rgba(220,38,38,0.92)] border border-[rgba(255,255,255,0.18)]',
                            'text-white text-[13px] font-semibold tracking-wide select-none',
                            'shadow-[0_8px_32px_rgba(220,38,38,0.45),0_2px_8px_rgba(0,0,0,0.3)]',
                            'cursor-pointer transition-all duration-200 hover:scale-105 hover:shadow-[0_12px_40px_rgba(220,38,38,0.60)]',
                            'animate-[v2FlyoutIn_0.18s_ease-out_both]',
                        ].join(' ')}
                        style={{
                            backdropFilter: 'blur(12px) saturate(1.3)',
                            WebkitBackdropFilter: 'blur(12px) saturate(1.3)',
                        }}
                        onClick={openModal}
                        title="Click to view transcription"
                        role="button"
                        aria-label="Recording in progress — click to open transcription"
                    >
                        <span className="relative flex h-2.5 w-2.5 shrink-0">
                            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white opacity-60" />
                            <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-white" />
                        </span>
                        <span className="leading-none">REC</span>
                        <span className="font-mono text-[12px] text-red-200 leading-none">{duration}</span>
                    </div>,
                    document.body
                )}

                <RecModal
                    isOpen={isModalOpen}
                    onCancel={cancelTranscription}
                    onClose={closeModal}
                    onSave={saveTranscriptionFromModal}
                    transcript={finalTranscript + interimTranscript}
                    loading={loading}
                    canSave={!!canSave}
                />
            </>
        );
    }
);

export default VoiceRecording;
